// bm-design-system: carousel
//
// A horizontal, snap-scrolling track that auto-advances on a timer, pauses while
// hovered or keyboard-focused, and reveals prev/next arrows on hover/focus. The
// children are the slides — each should set its own width plus `shrink-0
// snap-start`. No animation library; scrolling is native. SSR-safe: all timers and
// measurement run inside effects, the initial state is deterministic, and
// auto-advance is skipped when the user prefers reduced motion.
import * as React from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";

export interface CarouselProps {
  children: React.ReactNode;
  /** Auto-advance interval in ms. 0 disables auto-advance. Default 4000. */
  autoAdvanceMs?: number;
  /** Accessible label for the scrolling region. */
  ariaLabel?: string;
  /** Align the track's start with the container edge (so the first slide lines
   *  up with surrounding content) by lifting the prev arrow out of the flow into
   *  the left gutter. The arrow only shows where the gutter has room (xl+). */
  alignStart?: boolean;
  className?: string;
}

export function Carousel({
  children,
  autoAdvanceMs = 4000,
  ariaLabel = "Carousel",
  alignStart = false,
  className,
}: CarouselProps) {
  const trackRef = React.useRef<HTMLDivElement>(null);
  const [canLeft, setCanLeft] = React.useState(false);
  const [canRight, setCanRight] = React.useState(true);
  const [hovered, setHovered] = React.useState(false);
  const [focused, setFocused] = React.useState(false);
  const active = hovered || focused; // pause + reveal arrows while interacting

  const updateArrows = React.useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 8);
    setCanRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 8);
  }, []);

  const scrollByDir = React.useCallback((dir: 1 | -1) => {
    const el = trackRef.current;
    if (!el) return;
    const first = el.firstElementChild as HTMLElement | null;
    const amount = first ? first.offsetWidth + 16 : el.clientWidth * 0.9;
    el.scrollBy({ left: dir * amount, behavior: "smooth" });
  }, []);

  // Keep the arrow enabled/disabled state in sync with scroll position.
  React.useEffect(() => {
    updateArrows();
    const el = trackRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateArrows, { passive: true });
    window.addEventListener("resize", updateArrows);
    return () => {
      el.removeEventListener("scroll", updateArrows);
      window.removeEventListener("resize", updateArrows);
    };
  }, [updateArrows]);

  // Auto-advance, unless the user is interacting or prefers reduced motion.
  React.useEffect(() => {
    if (!autoAdvanceMs || active) return;
    const reduce =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return;
    const id = window.setInterval(() => {
      const el = trackRef.current;
      if (!el) return;
      const atEnd = el.scrollLeft >= el.scrollWidth - el.clientWidth - 8;
      if (atEnd) el.scrollTo({ left: 0, behavior: "smooth" });
      else scrollByDir(1);
    }, autoAdvanceMs);
    return () => window.clearInterval(id);
  }, [autoAdvanceMs, active, scrollByDir]);

  // Arrows sit in gutters beside the track (never over the cards). Shown whenever
  // that direction can scroll — not gated on hover, so they don't vanish as you
  // reach for them — and hidden on mobile where the track scrolls by swipe.
  const arrow =
    "h-10 w-10 shrink-0 cursor-pointer items-center justify-center rounded-full bg-accent text-page shadow-sm transition-opacity hover:bg-accent-display";
  // With alignStart, both arrows leave the flow and hang in the side gutters, so
  // the track spans the full width — the first slide aligns to the container's
  // left edge and the last slide reaches the right edge. Gutter arrows show only
  // at xl+, where the gutters are wide enough to hold them without overflow.
  const prevArrow = alignStart
    ? cn(arrow, "hidden xl:inline-flex absolute left-0 top-1/2 -translate-y-1/2 -translate-x-[calc(100%+0.75rem)]")
    : cn(arrow, "hidden sm:inline-flex");
  const nextArrow = alignStart
    ? cn(arrow, "hidden xl:inline-flex absolute right-0 top-1/2 -translate-y-1/2 translate-x-[calc(100%+0.75rem)]")
    : cn(arrow, "hidden sm:inline-flex");

  return (
    <div
      className={cn(
        "group flex w-full items-center gap-2 sm:gap-3",
        alignStart && "relative",
        className,
      )}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocusCapture={() => setFocused(true)}
      onBlurCapture={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) setFocused(false);
      }}
    >
      <button
        type="button"
        onClick={() => scrollByDir(-1)}
        aria-label="Previous"
        className={cn(
          prevArrow,
          canLeft ? "opacity-100" : "pointer-events-none opacity-0",
        )}
      >
        <ArrowLeft className="h-5 w-5" aria-hidden="true" />
      </button>

      <div
        ref={trackRef}
        role="region"
        aria-label={ariaLabel}
        aria-roledescription="carousel"
        className="no-scrollbar flex min-w-0 flex-1 gap-4 overflow-x-auto scroll-smooth py-2"
      >
        {children}
      </div>

      <button
        type="button"
        onClick={() => scrollByDir(1)}
        aria-label="Next"
        className={cn(
          nextArrow,
          canRight ? "opacity-100" : "pointer-events-none opacity-0",
        )}
      >
        <ArrowRight className="h-5 w-5" aria-hidden="true" />
      </button>
    </div>
  );
}
